// src/pages/AdminDashboard.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';

const AdminDashboard: React.FC = () => {
  const { user, role, loading } = useUserRole();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalClients: 0,
    totalTasks: 0,
    adminUsers: 0,
    staffUsers: 0,
    clientUsers: 0
  });

  useEffect(() => {
    if (!loading && role !== 'admin') {
      navigate('/client-dashboard');
    }
  }, [loading, role, navigate]);

  useEffect(() => {
    if (role === 'admin') {
      loadStats();
      
      // Set up real-time listeners for updates
      const usersUnsubscribe = onSnapshot(collection(db, 'users'), () => {
        loadStats();
      });
      
      const clientsUnsubscribe = onSnapshot(collection(db, 'clients'), () => {
        loadStats();
      });
      
      return () => {
        usersUnsubscribe();
        clientsUnsubscribe();
      };
    }
  }, [role]);

  const loadStats = async () => {
    try {
      // Get users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get clients
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      
      // Get tasks (if collection exists)
      let tasksCount = 0;
      try {
        const tasksSnapshot = await getDocs(collection(db, 'tasks'));
        tasksCount = tasksSnapshot.size;
      } catch (error) {
        // Tasks collection may not exist yet
      }

      const adminUsers = users.filter((u: any) => u.role === 'admin').length;
      const staffUsers = users.filter((u: any) => u.role === 'staff').length;
      const clientUsers = users.filter((u: any) => u.role === 'client').length;

      setStats({
        totalUsers: users.length,
        totalClients: clientsSnapshot.size,
        totalTasks: tasksCount,
        adminUsers,
        staffUsers,
        clientUsers
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (role !== 'admin') {
    return null;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <h1>Admin Dashboard</h1>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Welcome, {user?.email}. Manage your system from here.
        </p>

        {/* Statistics Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <StatCard 
            title="Total Users" 
            value={stats.totalUsers} 
            color="#007bff"
            icon="👥"
          />
          <StatCard 
            title="Total Clients" 
            value={stats.totalClients} 
            color="#28a745"
            icon="🏢"
          />
          <StatCard 
            title="Total Tasks" 
            value={stats.totalTasks} 
            color="#ffc107"
            icon="📋"
          />
        </div>

        {/* User Breakdown */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <StatCard 
            title="Admin Users" 
            value={stats.adminUsers} 
            color="#dc3545"
            icon="👑"
          />
          <StatCard 
            title="Staff Users" 
            value={stats.staffUsers} 
            color="#ffc107"
            icon="👨‍💼"
          />
          <StatCard 
            title="Client Users" 
            value={stats.clientUsers} 
            color="#28a745"
            icon="👤"
          />
        </div>

        {/* Quick Actions */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginTop: 0 }}>Quick Actions</h2>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <ActionButton 
              onClick={() => navigate('/user-management')}
              label="Manage Users"
              icon="👥"
            />
            <ActionButton 
              onClick={() => navigate('/clients')}
              label="View Clients"
              icon="🏢"
            />
            <ActionButton 
              onClick={() => navigate('/tasks')}
              label="Manage Tasks"
              icon="📋"
            />
            <ActionButton 
              onClick={() => navigate('/admin/documents')}
              label="Manage Documents"
              icon="📄"
            />
            <ActionButton 
              onClick={() => navigate('/admin/entity-formation')}
              label="Entity Formation Forms"
              icon="🏢"
            />
            <ActionButton 
              onClick={() => navigate('/admin/privacy-requests')}
              label="Privacy Requests"
              icon="🔐"
            />
            <ActionButton 
              onClick={() => navigate('/admin/glba')}
              label="GLBA Compliance"
              icon="💰"
            />
            <ActionButton 
              onClick={() => navigate('/admin/audit-logs')}
              label="Audit Logs"
              icon="📋"
            />
            <ActionButton 
              onClick={() => navigate('/security-test')}
              label="Test Security"
              icon="🔒"
            />
          </div>
        </div>

        {/* System Info */}
        <div style={{ 
          backgroundColor: '#e9ecef', 
          padding: '1rem', 
          borderRadius: '5px',
          fontSize: '0.875rem'
        }}>
          <strong>System Status:</strong> All systems operational ✅
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  color: string;
  icon: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, color, icon }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
          {title}
        </div>
        <div style={{ fontSize: '2rem', fontWeight: 'bold', color }}>
          {value}
        </div>
      </div>
      <div style={{ fontSize: '2.5rem' }}>
        {icon}
      </div>
    </div>
  </div>
);

interface ActionButtonProps {
  onClick: () => void;
  label: string;
  icon: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({ onClick, label, icon }) => (
  <button
    onClick={onClick}
    style={{
      padding: '0.75rem 1.5rem',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '1rem'
    }}
  >
    <span>{icon}</span>
    <span>{label}</span>
  </button>
);

export default AdminDashboard;

